import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getOpenRegistrationLiveCounts,
  type OpenRegistrationResult,
  type OpenRegistrationRow,
} from "../../services/eventExecutiveSummary.service";
import { EVENT_TYPES } from "../../utils/constants";
import {
  CAP_LABELS,
  CAP_UNITS,
  type BindingCap,
  changeSinceOpened,
  formatAgo,
  formatChange,
  formatEventStartInZone,
  placesLeft,
} from "../../utils/openRegistration";

// Owner 2026-10-12: "show a live and updating count of registrations for events which are collecting
// registrations but haven't happened yet." The server decides which events qualify (published landing
// page, not started, not cancelled/completed, registration not closed, deadline not passed — migration
// 2026-10-12-430) and counts registrations LIVE from event_attendees with the Overview funnel's
// canonical contract. This panel polls every 30 s; TanStack Query pauses the interval while the
// browser tab is hidden (refetchIntervalInBackground defaults to false) and refetches on focus.
// event_attendees is not in the realtime publication, so polling is the mechanism by design.

export const OPEN_REGISTRATION_POLL_MS = 30_000;

const count = new Intl.NumberFormat("en-US");

const typeLabel = (value: string): string =>
  EVENT_TYPES.find((t) => t.value === value)?.label ?? value;

interface Props {
  eventType?: string;
  states?: string[];
}

const ChangeBadge: React.FC<{ delta: number }> = ({ delta }) => {
  const text = formatChange(delta);
  if (!text) {
    return null;
  }
  return (
    <span
      className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-medium ${
        delta > 0
          ? "bg-green-100 text-green-800"
          : "bg-amber-100 text-amber-800"
      }`}
      title="Change since you opened this page"
    >
      {text}
    </span>
  );
};

const EventBadges: React.FC<{ row: OpenRegistrationRow }> = ({ row }) => (
  <>
    {row.event_status === "postponed" && (
      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
        Postponed
      </span>
    )}
    {/* invite_gated: the landing page refuses anyone without a personal invitation link. */}
    {row.registration_mode === "invite_gated" && (
      <span
        className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800"
        title="Only people with a personal invitation link can register"
      >
        Invite only
      </span>
    )}
  </>
);

const capTitle = (cap: BindingCap): string =>
  `${CAP_LABELS[cap.kind]}: ${cap.used} of ${cap.limit} used (${CAP_UNITS[cap.kind]})`;

// Only caps that apply to a channel the event still accepts count here (placesLeft): an invite-only
// event is never "Full" because of the public sign-up limit, which the register handler does not
// apply to personal invitation links.
const PlacesLeft: React.FC<{ row: OpenRegistrationRow }> = ({ row }) => {
  const p = placesLeft(row);
  if (p.state === "no_limit") {
    return (
      <span className="text-gray-500" data-testid="open-registration-places">
        No limit
      </span>
    );
  }
  if (p.state === "public_full") {
    const inv = p.invitation;
    return (
      <span
        data-testid="open-registration-places"
        title={`${capTitle(p.cap)}. Only valid, unused personal invitation links can still register${
          inv ? ` (${capTitle(inv)})` : ""
        }.`}
      >
        <span className="whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
          Public sign-ups full
        </span>
        <span className="block text-xs text-gray-500">
          {inv
            ? `Invitations: ${count.format(inv.remaining)} of ${count.format(inv.limit)} left (${CAP_LABELS[inv.kind]})`
            : "Only valid personal invitation links can register"}
        </span>
      </span>
    );
  }
  const cap = p.cap;
  return (
    <span data-testid="open-registration-places" title={capTitle(cap)}>
      {p.state === "full" ? (
        <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800">
          Full
        </span>
      ) : (
        <span className="font-medium text-gray-900">
          {count.format(cap.remaining)}
        </span>
      )}
      <span className="text-gray-500"> of {count.format(cap.limit)}</span>
      <span className="block text-xs text-gray-500">
        {CAP_LABELS[cap.kind]}
      </span>
    </span>
  );
};

const OpenForRegistrationSection: React.FC<Props> = ({ eventType, states }) => {
  const [open, setOpen] = useState(true);
  // First registered_rows seen per event during this page visit -> "change since opened".
  const baseline = useRef<Map<string, number>>(new Map());

  const { data, isLoading, error, dataUpdatedAt, isFetching } =
    useQuery<OpenRegistrationResult>({
      queryKey: [
        "event-open-registration-live",
        eventType ?? null,
        states ?? null,
      ],
      queryFn: () => getOpenRegistrationLiveCounts({ eventType, states }),
      enabled: open,
      refetchInterval: OPEN_REGISTRATION_POLL_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      staleTime: 10_000,
    });

  const hasData = data !== undefined;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  for (const row of rows) {
    if (!baseline.current.has(row.event_id)) {
      baseline.current.set(row.event_id, row.registered_rows);
    }
  }
  const delta = (row: OpenRegistrationRow) =>
    changeSinceOpened(baseline.current.get(row.event_id), row.registered_rows);

  const filtered = Boolean(eventType) || Boolean(states && states.length);

  return (
    <section
      aria-labelledby="open-registration-heading"
      className="rounded-lg border border-gray-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-2 text-left"
        >
          <span aria-hidden="true" className="text-gray-500">
            {open ? "▾" : "▸"}
          </span>
          <h2
            id="open-registration-heading"
            className="text-base font-semibold text-gray-900"
          >
            Open for registration
          </h2>
          {data && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
              {count.format(total)}
            </span>
          )}
        </button>
        {open && data && (
          <span
            className="flex items-center gap-1.5 text-xs text-gray-500"
            data-testid="open-registration-updated"
          >
            <span
              aria-hidden="true"
              className={`inline-block h-2 w-2 rounded-full ${
                isFetching ? "bg-amber-400" : "bg-green-500"
              }`}
            />
            Live · updated{" "}
            {new Date(dataUpdatedAt).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              second: "2-digit",
            })}
          </span>
        )}
      </div>

      {open && (
        <div className="border-t border-gray-200">
          <p className="px-4 pt-3 text-xs text-gray-500">
            Upcoming events whose registration page is published and still
            taking registrations. Counts are live: they refresh every 30 seconds
            while this page is open (the tables below refresh about every five
            minutes). Registered counts registration records and People counts
            everyone on them — a party of three is one registration, three
            people — the same numbers as the event's Overview. Walk-ins and
            cancelled registrations are not counted. Places left is the tightest
            limit that applies to the people who can still register. Invite only
            events accept only people with a personal invitation link, so the
            public sign-up limit does not apply to them; “Public sign-ups full”
            means the public registration page is closed and only valid, unused
            personal invitation links can still register. Uses the event type
            and state filters above.
          </p>
          {/* A failed poll must not wipe the live table: TanStack keeps the last good `data`, so keep
              showing it with a small stale notice. The full error box is only for "never loaded". */}
          {error && hasData && (
            <p
              role="status"
              className="mx-4 mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              data-testid="open-registration-stale"
            >
              Couldn't refresh just now — showing counts from{" "}
              {new Date(dataUpdatedAt).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
              })}
              . Retrying automatically.
            </p>
          )}
          {error && !hasData && (
            <div
              role="alert"
              className="m-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {error instanceof Error
                ? error.message
                : "Unable to load open registrations"}
            </div>
          )}
          {!error && !hasData && isLoading && (
            <p className="px-4 py-4 text-sm text-gray-500">
              Loading open registrations…
            </p>
          )}
          {hasData && rows.length === 0 && (
            <p
              className="px-4 py-4 text-sm text-gray-500"
              data-testid="open-registration-empty"
            >
              {filtered
                ? "No upcoming events matching these filters are taking registrations right now."
                : "No upcoming events are taking registrations right now."}
            </p>
          )}
          {hasData && rows.length > 0 && (
            <>
              {/* Phone: one card per event. */}
              <ul className="divide-y divide-gray-100 md:hidden">
                {rows.map((row) => (
                  <li key={row.event_id} className="px-4 py-3 text-sm">
                    <Link
                      to={`/events/${row.event_id}`}
                      className="font-medium text-primary-700 hover:underline"
                    >
                      {row.event_title}
                    </Link>
                    <EventBadges row={row} />
                    <div className="mt-0.5 text-gray-700">
                      {row.date_tbd
                        ? "Date TBD"
                        : formatEventStartInZone(
                            row.event_start_at,
                            row.event_timezone,
                          )}
                    </div>
                    <div className="mt-1 text-gray-900">
                      <span className="font-semibold">
                        {count.format(row.registered_rows)}
                      </span>{" "}
                      registered
                      <ChangeBadge delta={delta(row)} /> ·{" "}
                      {count.format(row.registered_headcount)}{" "}
                      {row.registered_headcount === 1 ? "person" : "people"} ·{" "}
                      {count.format(row.registered_rows_24h)} in last 24 h
                    </div>
                    <div className="mt-0.5 text-xs text-gray-600">
                      Places left: <PlacesLeft row={row} />
                    </div>
                  </li>
                ))}
              </ul>
              {/* Tablet/desktop: table. */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-4 py-2">Event</th>
                      <th className="px-4 py-2">Starts (event time)</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">State</th>
                      <th className="px-4 py-2 text-right">Registered</th>
                      <th className="px-4 py-2 text-right">People</th>
                      <th className="px-4 py-2 text-right">Last 24 h</th>
                      <th className="px-4 py-2">Places left</th>
                      <th className="px-4 py-2">Last registration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => (
                      <tr
                        key={row.event_id}
                        className="hover:bg-gray-50"
                        data-testid="open-registration-row"
                      >
                        <td className="px-4 py-2">
                          <Link
                            to={`/events/${row.event_id}`}
                            className="font-medium text-primary-700 hover:underline"
                          >
                            {row.event_title}
                          </Link>
                          <EventBadges row={row} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-2">
                          {row.date_tbd ? (
                            <span title="The date is not final">Date TBD</span>
                          ) : (
                            formatEventStartInZone(
                              row.event_start_at,
                              row.event_timezone,
                            )
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {typeLabel(row.event_type)}
                        </td>
                        <td className="px-4 py-2">{row.event_state || "—"}</td>
                        <td
                          className="whitespace-nowrap px-4 py-2 text-right font-semibold text-gray-900"
                          data-testid="open-registration-registered"
                        >
                          {count.format(row.registered_rows)}
                          <ChangeBadge delta={delta(row)} />
                        </td>
                        <td
                          className="px-4 py-2 text-right"
                          title={`${row.registered_member_headcount} members, ${row.registered_nonmember_headcount} non-members (where the registrant said so)`}
                        >
                          {count.format(row.registered_headcount)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {count.format(row.registered_rows_24h)}
                        </td>
                        <td className="px-4 py-2">
                          <PlacesLeft row={row} />
                        </td>
                        <td
                          className="whitespace-nowrap px-4 py-2 text-gray-700"
                          title={
                            row.last_registered_at
                              ? new Date(
                                  row.last_registered_at,
                                ).toLocaleString()
                              : undefined
                          }
                        >
                          {formatAgo(row.last_registered_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > rows.length && (
                <p className="px-4 py-2 text-xs text-gray-500">
                  Showing the first {rows.length} of {count.format(total)}.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default OpenForRegistrationSection;
